import { ACTIONENTRY_SYNC_KEY, ACTIONENTRY_SYNC_POLL_INTERVAL_MS, syncActionEntries } from './ActionEntrySync.js';
import { ACTIONTYPE_SYNC_KEY, ACTIONTYPE_SYNC_POLL_INTERVAL_MS, syncActionTypes } from './ActionTypeSync.js';
import { COMMENT_CACHE_PRUNE_INTERVAL_MS, COMMENT_CACHE_PRUNE_KEY, pruneStaleComments } from './CommentCachePruning.js';
import { GRAPH_SYNC_KEY, GRAPH_SYNC_POLL_INTERVAL_MS, syncGraphs } from './GraphSync.js';
import { registerGlobalSync } from './GlobalSyncRegistry.js';
import { PASSAGE_SYNC_KEY, PASSAGE_SYNC_POLL_INTERVAL_MS, syncPassages } from './PassageSync.js';
import { PLAN_SYNC_KEY, PLAN_SYNC_POLL_INTERVAL_MS, syncPlans } from './PlanSync.js';
import { PRAYER_SYNC_KEY, PRAYER_SYNC_POLL_INTERVAL_MS, syncPrayers } from './PrayerSync.js';
import {
  SERVER_MESSAGE_SYNC_KEY,
  SERVER_MESSAGE_SYNC_POLL_INTERVAL_MS,
  syncServerMessages,
} from './ServerMessageSync.js';
import { SETTING_SYNC_POLL_INTERVAL_MS, SETTINGS_SYNC_KEY, syncSettings } from './SettingSync.js';

/**
 * Single place to plug a new collection into the global (schedule-based,
 * not per-item) sync engine - add its own sync module alongside
 * `SettingSync.ts`, then register it here with a key and poll interval.
 * That module's own local writes (e.g. `queueLocalSettingUpdate`) can then
 * call `triggerGlobalSync(key)` (from `GlobalSyncMiddleware.ts`) to push
 * soon after an edit instead of waiting for the poll interval.
 *
 * A registered "source" doesn't have to talk to the server, either - it
 * just needs to be an idempotent async task worth running on a schedule
 * while logged in. `pruneStaleComments` is local-only housekeeping that
 * piggybacks on the same engine for exactly that reason.
 *
 * On cold start, `GlobalSyncMiddleware.ts`'s startGlobalSyncs runs sources
 * in ascending `priority` tiers (default 0), all sources within a tier in
 * parallel. Leave `priority` unset unless a source's *pull* genuinely
 * depends on another source's data already being local - it only slows
 * cold start down.
 */
registerGlobalSync({ key: SETTINGS_SYNC_KEY, intervalMs: SETTING_SYNC_POLL_INTERVAL_MS, sync: syncSettings });
registerGlobalSync({ key: COMMENT_CACHE_PRUNE_KEY, intervalMs: COMMENT_CACHE_PRUNE_INTERVAL_MS, sync: pruneStaleComments });
registerGlobalSync({ key: PASSAGE_SYNC_KEY, intervalMs: PASSAGE_SYNC_POLL_INTERVAL_MS, sync: syncPassages });
registerGlobalSync({ key: PRAYER_SYNC_KEY, intervalMs: PRAYER_SYNC_POLL_INTERVAL_MS, sync: syncPrayers });
registerGlobalSync({ key: PLAN_SYNC_KEY, intervalMs: PLAN_SYNC_POLL_INTERVAL_MS, sync: syncPlans });
registerGlobalSync({ key: ACTIONTYPE_SYNC_KEY, intervalMs: ACTIONTYPE_SYNC_POLL_INTERVAL_MS, sync: syncActionTypes });
registerGlobalSync({
  key: SERVER_MESSAGE_SYNC_KEY,
  intervalMs: SERVER_MESSAGE_SYNC_POLL_INTERVAL_MS,
  sync: syncServerMessages,
});

// action entries reference action_type_id, and a graph's embedded data
// points reference action type ids too (see GraphSync.ts's pushCreate for
// the known limitation this doesn't fully solve on its own) - both get
// priority 1 so action types' own tier-0 sync lands first on cold start
registerGlobalSync({
  key: ACTIONENTRY_SYNC_KEY,
  intervalMs: ACTIONENTRY_SYNC_POLL_INTERVAL_MS,
  sync: syncActionEntries,
  priority: 1,
});
registerGlobalSync({ key: GRAPH_SYNC_KEY, intervalMs: GRAPH_SYNC_POLL_INTERVAL_MS, sync: syncGraphs, priority: 1 });
